import { api } from "./auth";

/**
 * Admin blog API client — requires authenticated admin/master_admin session.
 * All endpoints hit /api/admin/blog/* on the backend.
 */
export const blogApi = {
    list(status) {
        const params = {};
        if (status) params.status = status;
        return api.get("/admin/blog/posts", { params }).then((r) => r.data);
    },
    get(id) {
        return api.get(`/admin/blog/posts/${id}`).then((r) => r.data);
    },
    create(payload) {
        return api.post("/admin/blog/posts", payload).then((r) => r.data);
    },
    update(id, payload) {
        return api.put(`/admin/blog/posts/${id}`, payload).then((r) => r.data);
    },
    approve(id) {
        return api.post(`/admin/blog/posts/${id}/approve`).then((r) => r.data);
    },
    publishNow(id) {
        return api.post(`/admin/blog/posts/${id}/publish-now`).then((r) => r.data);
    },
    unpublish(id) {
        return api.post(`/admin/blog/posts/${id}/unpublish`).then((r) => r.data);
    },
    remove(id) {
        return api.delete(`/admin/blog/posts/${id}`).then((r) => r.data);
    },
    importJson(posts) {
        return api.post("/admin/blog/import", { posts }).then((r) => r.data);
    },
    getSettings() {
        return api.get("/admin/blog/settings").then((r) => r.data);
    },
    updateSettings(payload) {
        return api.put("/admin/blog/settings", payload).then((r) => r.data);
    },
};

/* Public (unauthenticated) endpoints */

export async function fetchLatestBlogPosts(limit = 3) {
    const res = await api.get("/blog/public/latest", { params: { limit } });
    return res.data;
}

export const BLOG_CATEGORIES = [
    "Life",
    "Final Expense",
    "IUL",
    "Annuities",
    "Health",
    "Medicare",
    "Jarvis",
    "Florida",
];

export const BLOG_STATUSES = ["draft", "scheduled", "published", "unpublished"];

/**
 * Minimal in-browser Markdown preview. Feature-matched to the server-side
 * `markdown-it-py` for the fields the admin editor shows: headings, bold,
 * italic, lists, blockquotes, inline code, and paragraphs. For final-fidelity
 * rendering the admin should use the real preview endpoint (/blog/:slug?preview=1).
 */
export function renderMarkdownPreview(text) {
    if (!text) return "";
    const esc = (s) =>
        s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

    const lines = esc(text).split(/\r?\n/);
    const out = [];
    let inList = false;
    let listTag = "ul";
    let inBlockquote = false;

    const closeList = () => {
        if (inList) {
            out.push(`</${listTag}>`);
            inList = false;
        }
    };
    const closeBq = () => {
        if (inBlockquote) {
            out.push("</blockquote>");
            inBlockquote = false;
        }
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            closeList();
            closeBq();
            continue;
        }
        const h = line.match(/^(#{1,4})\s+(.+)$/);
        if (h) {
            closeList();
            closeBq();
            const level = h[1].length;
            out.push(`<h${level}>${h[2]}</h${level}>`);
            continue;
        }
        const bq = line.match(/^>\s?(.*)$/);
        if (bq) {
            closeList();
            if (!inBlockquote) {
                out.push("<blockquote>");
                inBlockquote = true;
            }
            out.push(`<p>${bq[1]}</p>`);
            continue;
        }
        const ul = line.match(/^[-*]\s+(.+)$/);
        const ol = line.match(/^(\d+)\.\s+(.+)$/);
        if (ul || ol) {
            closeBq();
            const tag = ul ? "ul" : "ol";
            if (!inList || listTag !== tag) {
                closeList();
                listTag = tag;
                out.push(`<${tag}>`);
                inList = true;
            }
            out.push(`<li>${ul ? ul[1] : ol[2]}</li>`);
            continue;
        }
        closeList();
        closeBq();
        out.push(`<p>${line}</p>`);
    }
    closeList();
    closeBq();

    // Inline: **bold**, *italic*, `code`, [text](url).
    return out
        .join("\n")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/(^|\W)\*(.+?)\*(?=\W|$)/g, "$1<em>$2</em>")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" rel="noopener noreferrer" target="_blank">$1</a>');
}

export const BLOG_IMPORT_EXAMPLE = {
    title: "How much life insurance do I actually need?",
    slug: "how-much-life-insurance-do-i-need",
    meta_title:
        "How much life insurance do I actually need? | Moving Mountains Advisors",
    meta_description:
        "Three plain-English frameworks for sizing a life insurance policy — income replacement, debt-plus-education, and the DIME rule.",
    category: "Life",
    tags: ["term", "sizing", "family"],
    hero_image_url: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1600",
    hero_image_alt: "A multigenerational family at a kitchen table",
    body_markdown:
        "## Start with income replacement\n\nThe easiest rule of thumb is ten times your household income if you want to replace earnings for a decade.\n\n## Add debts and future obligations\n\nMortgage balance, student loans, and projected college costs matter too. Add them all up.\n\n## The DIME rule\n\n**D**ebt, **I**ncome, **M**ortgage, **E**ducation. Sum each category and that is a defensible floor.",
    faq: [
        {
            q: "Is 10x income enough?",
            a: "Often, yes — but families with young kids or a longer mortgage window usually need more.",
        },
        {
            q: "Can I buy too much coverage?",
            a: "Yes — carriers underwrite the amount against your income and net worth, so overshooting may be declined.",
        },
    ],
    disclaimer:
        "This article is educational and not an offer of coverage or a guarantee of approval or rates.",
    publish_at: null,
};
